import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload, Search, X, Archive, Trash2, Edit3, RefreshCw, Plus, Image as ImageIcon, AlertCircle } from "lucide-react";

const CATEGORY_LABELS = {
  status_lifecycle: "01 · Status & Lifecycle",
  payments_billing: "02 · Payments & Billing",
  capacity_availability: "03 · Capacity & Availability",
  communications: "04 · Communications",
  portal_badges: "05 · Portal Badges & Recognition",
  portal_navigation: "06 · Portal Navigation",
  grower_operations: "07 · Grower Operations",
  console_system: "08 · Bloom Club Admin / System",
  subscriber_rewards: "09 · Subscriber Rewards & Milestones",
  community_events: "10 · Community & Events",
  utility_core: "11 · Utility / Core Interface",
  website_graphics: "Website Graphics",
  banners_hero: "Banners & Hero",
  territory_maps: "Territory Maps",
  certificates: "Certificates",
  promotional: "Promotional",
  training: "Training",
  seasonal_campaign: "Seasonal Campaign",
  other: "Other",
};

export default function ConsoleMedia() {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (activeCategory) params.set("category", activeCategory);
    if (showArchived) params.set("archived", "true");
    const { data } = await api.get(`/admin/media?${params.toString()}`);
    setAssets(data.assets || []);
    setLoading(false);
  };

  const loadCategories = async () => {
    const { data } = await api.get("/admin/media/categories");
    setCategories(data.categories || []);
  };

  useEffect(() => {
    loadCategories();
  }, [assets.length]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeCategory, showArchived]);

  const totalAssets = useMemo(() => categories.reduce((s, c) => s + c.count, 0), [categories]);

  return (
    <div data-testid="console-media">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-none">Media Library</h1>
          <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">
            Stickers, status icons, banners, certificates and every visual asset, in one place.
          </p>
        </div>
        <button onClick={() => setShowUpload(true)} className="bloom-btn bloom-btn-primary" data-testid="media-upload-btn">
          <Plus size={14} /> Upload Asset
        </button>
        <label className="bloom-btn bloom-btn-outline cursor-pointer ml-2" data-testid="media-zip-btn" title="Drop a ZIP of PNGs, auto categorised by filename">
          <Upload size={14} /> Import ZIP
          <input
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 50 * 1024 * 1024) { alert("ZIP must be 50MB or smaller."); return; }
              const reader = new FileReader();
              reader.onload = async () => {
                try {
                  const { data } = await api.post("/admin/media/zip-import", { data_url: reader.result, default_category: "other" });
                  alert(`Imported ${data.created_count} asset(s). Skipped ${data.skipped_count}.`);
                  load();
                  loadCategories();
                } catch (err) {
                  alert("Import failed: " + (err.response?.data?.detail || err.message));
                }
              };
              reader.readAsDataURL(file);
            }}
          />
        </label>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar: categories */}
        <aside className="col-span-12 md:col-span-3" data-testid="media-categories">
          <div className="bloom-label mb-3">Categories</div>
          <button
            onClick={() => setActiveCategory("")}
            className={`w-full text-left px-3 py-2 mb-1 text-sm font-serif flex items-center justify-between transition ${activeCategory === "" ? "bg-[var(--bloom-ink)] text-[#FAF4FA]" : "hover:bg-[var(--bloom-surface)]"}`}
            data-testid="media-cat-all"
          >
            <span>All assets</span>
            <span className="text-xs opacity-70">{totalAssets}</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCategory(c.id)}
              className={`w-full text-left px-3 py-2 mb-1 text-sm font-serif flex items-center justify-between transition ${activeCategory === c.id ? "bg-[var(--bloom-ink)] text-[#FAF4FA]" : "hover:bg-[var(--bloom-surface)]"}`}
              data-testid={`media-cat-${c.id}`}
            >
              <span>{CATEGORY_LABELS[c.id] || c.id}</span>
              <span className="text-xs opacity-70">{c.count}</span>
            </button>
          ))}
          <div className="mt-6 pt-4 border-t border-[var(--bloom-border)]">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="bloom-check" data-testid="media-show-archived" />
              <span>Show archived</span>
            </label>
          </div>
        </aside>

        {/* Main: search + grid */}
        <main className="col-span-12 md:col-span-9">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 flex items-center gap-2 border border-[var(--bloom-border)] px-3 py-2 bg-white/50">
              <Search size={14} className="text-[var(--bloom-ink-muted)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search name, status key, tag, description…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="media-search"
              />
              {q && <button onClick={() => { setQ(""); setTimeout(load, 0); }}><X size={14} /></button>}
            </div>
            <button onClick={load} className="bloom-btn bloom-btn-outline" data-testid="media-refresh"><RefreshCw size={14} /> Refresh</button>
          </div>

          {loading ? (
            <div className="text-center py-24 font-serif italic text-[var(--bloom-ink-muted)]">Loading…</div>
          ) : assets.length === 0 ? (
            <div className="bloom-card p-14 text-center" data-testid="media-empty">
              <ImageIcon className="mx-auto mb-3 text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mb-2">Your library is empty.</h3>
              <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6 max-w-md mx-auto">
                Upload your first sticker, status icon or banner. Future asset packs will live here.
              </p>
              <button onClick={() => setShowUpload(true)} className="bloom-btn bloom-btn-primary"><Plus size={14} /> Upload Asset</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
              {assets.map((a) => (
                <article key={a.id} onClick={() => setEditing(a)} className="cursor-pointer border border-[var(--bloom-border)] bg-white/40 hover:bg-white/70 transition" data-testid={`media-asset-${a.id}`}>
                  <div className="aspect-square flex items-center justify-center p-4 bg-[var(--bloom-surface)]" style={{ backgroundImage: "linear-gradient(45deg, transparent 49%, rgba(0,0,0,0.04) 49%, rgba(0,0,0,0.04) 51%, transparent 51%), linear-gradient(-45deg, transparent 49%, rgba(0,0,0,0.04) 49%, rgba(0,0,0,0.04) 51%, transparent 51%)", backgroundSize: "16px 16px" }}>
                    <img src={a.data_url} alt={a.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="p-3">
                    <div className="font-serif text-base leading-tight truncate" title={a.name}>{a.name}</div>
                    <div className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-muted)] mt-1 truncate">
                      {CATEGORY_LABELS[a.category]?.split(" · ").pop() || a.category}
                    </div>
                    {a.status_key && (
                      <div className="text-[10px] font-mono text-[var(--bloom-accent)] mt-1 truncate" title={a.status_key}>{a.status_key}</div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </main>
      </div>

      {showUpload && (
        <MediaUploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); loadCategories(); }}
        />
      )}
      {editing && (
        <MediaEditDrawer
          asset={editing}
          onClose={() => setEditing(null)}
          onUpdated={(a) => { setAssets((arr) => arr.map((x) => x.id === a.id ? a : x)); setEditing(a); }}
          onDeleted={(id) => { setAssets((arr) => arr.filter((x) => x.id !== id)); setEditing(null); loadCategories(); }}
        />
      )}
    </div>
  );
}

// ============== Upload modal ==============
function MediaUploadModal({ onClose, onUploaded }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("status_lifecycle");
  const [subcategory, setSubcategory] = useState("");
  const [statusKey, setStatusKey] = useState("");
  const [tags, setTags] = useState("");
  const [description, setDescription] = useState("");
  const [dataUrl, setDataUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("File must be 5MB or smaller."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setDataUrl(reader.result);
      setFileName(file.name);
      if (!name) setName(file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!dataUrl) { setErr("Choose an image file first."); return; }
    if (!name.trim()) { setErr("Give the asset a name."); return; }
    setBusy(true);
    try {
      await api.post("/admin/media", {
        name: name.trim(),
        category,
        subcategory: subcategory.trim(),
        status_key: statusKey.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        description: description.trim(),
        data_url: dataUrl,
        file_name: fileName,
      });
      onUploaded?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="media-upload-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <h2 className="font-serif text-2xl">Upload Asset</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-8 space-y-5">
          {!dataUrl ? (
            <label className="border border-dashed border-[var(--bloom-border)] p-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-[var(--bloom-surface)]" data-testid="media-dropzone">
              <Upload className="text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
              <div className="text-sm">Click to choose an image</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">PNG transparency preserved · 5MB max</div>
              <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="media-file-input" />
            </label>
          ) : (
            <div className="border border-[var(--bloom-border)] p-4 flex items-center gap-4">
              <div className="w-24 h-24 flex items-center justify-center bg-[var(--bloom-surface)]" style={{ backgroundImage: "linear-gradient(45deg, transparent 49%, rgba(0,0,0,0.04) 49%, rgba(0,0,0,0.04) 51%, transparent 51%), linear-gradient(-45deg, transparent 49%, rgba(0,0,0,0.04) 49%, rgba(0,0,0,0.04) 51%, transparent 51%)", backgroundSize: "16px 16px" }}>
                <img src={dataUrl} alt="" className="max-w-full max-h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{fileName}</div>
                <button type="button" onClick={() => { setDataUrl(""); setFileName(""); }} className="text-xs underline mt-2">Choose different file</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Asset name *"><input value={name} onChange={(e) => setName(e.target.value)} className="bloom-input" data-testid="media-field-name" /></Field>
            <Field label="Category *">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="bloom-input" data-testid="media-field-category">
                {Object.keys(CATEGORY_LABELS).map((k) => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
              </select>
            </Field>
            <Field label="Subcategory" hint="Optional grouping inside the category"><input value={subcategory} onChange={(e) => setSubcategory(e.target.value)} className="bloom-input" placeholder="e.g. grower_status" data-testid="media-field-subcategory" /></Field>
            <Field label="Status key" hint='e.g. grower.active · delivery.packed'><input value={statusKey} onChange={(e) => setStatusKey(e.target.value)} className="bloom-input" data-testid="media-field-status-key" /></Field>
            <div className="md:col-span-2">
              <Field label="Tags" hint="Comma separated"><input value={tags} onChange={(e) => setTags(e.target.value)} className="bloom-input" placeholder="grower, active, sticker" data-testid="media-field-tags" /></Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Description"><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="bloom-input" style={{ resize: "vertical" }} data-testid="media-field-description" /></Field>
            </div>
          </div>

          {err && <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic"><AlertCircle size={14} /> {err}</div>}

          <div className="flex justify-end gap-3 border-t border-[var(--bloom-border)] pt-4">
            <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy || !dataUrl} className="bloom-btn bloom-btn-primary" data-testid="media-submit">{busy ? "Uploading…" : "Upload"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============== Edit drawer ==============
function MediaEditDrawer({ asset, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState({
    name: asset.name,
    category: asset.category,
    subcategory: asset.subcategory || "",
    status_key: asset.status_key || "",
    tags: (asset.tags || []).join(", "),
    description: asset.description || "",
    archived: !!asset.archived,
  });
  const [busy, setBusy] = useState(false);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setForm({
      name: asset.name,
      category: asset.category,
      subcategory: asset.subcategory || "",
      status_key: asset.status_key || "",
      tags: (asset.tags || []).join(", "),
      description: asset.description || "",
      archived: !!asset.archived,
    });
  }, [asset.id]);

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.patch(`/admin/media/${asset.id}`, {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onUpdated(data.asset);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("File must be 5MB or smaller."); return; }
    setReplaceBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/admin/media/${asset.id}/replace`, { data_url: reader.result, file_name: file.name });
        onUpdated(data.asset);
      } catch (err) {
        setErr(formatApiError(err.response?.data?.detail));
      } finally {
        setReplaceBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete "${asset.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/media/${asset.id}`);
    onDeleted(asset.id);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm" onClick={onClose} data-testid="media-edit-drawer">
      <div className="absolute right-0 top-0 bottom-0 w-full md:w-[640px] bg-[var(--bloom-bg)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
          <h2 className="font-serif text-xl">Edit Asset</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]"><X size={16} /></button>
        </div>
        <div className="p-8">
          <div className="border border-[var(--bloom-border)] p-4 mb-6 flex items-center gap-4">
            <div className="w-32 h-32 flex items-center justify-center bg-[var(--bloom-surface)]" style={{ backgroundImage: "linear-gradient(45deg, transparent 49%, rgba(0,0,0,0.04) 49%, rgba(0,0,0,0.04) 51%, transparent 51%), linear-gradient(-45deg, transparent 49%, rgba(0,0,0,0.04) 49%, rgba(0,0,0,0.04) 51%, transparent 51%)", backgroundSize: "16px 16px" }}>
              <img src={asset.data_url} alt={asset.name} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[var(--bloom-ink-muted)]">{asset.file_name} · {fmtSize(asset.size)} · v{asset.version}</div>
              <label className="bloom-btn bloom-btn-outline text-xs mt-3 cursor-pointer inline-flex">
                <Upload size={12} /> {replaceBusy ? "Replacing…" : "Replace file"}
                <input type="file" accept="image/*" onChange={onReplace} className="hidden" />
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bloom-input" /></Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bloom-input">
                {Object.keys(CATEGORY_LABELS).map((k) => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Subcategory"><input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="bloom-input" /></Field>
              <Field label="Status key"><input value={form.status_key} onChange={(e) => setForm({ ...form, status_key: e.target.value })} className="bloom-input" /></Field>
            </div>
            <Field label="Tags"><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bloom-input" placeholder="comma, separated" /></Field>
            <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bloom-input" style={{ resize: "vertical" }} /></Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.archived} onChange={(e) => setForm({ ...form, archived: e.target.checked })} className="bloom-check" />
              <span>Archived (hidden from default lists)</span>
            </label>
          </div>

          {err && <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic"><AlertCircle size={14} /> {err}</div>}

          <div className="mt-8 flex justify-between border-t border-[var(--bloom-border)] pt-5">
            <button onClick={remove} className="bloom-btn bloom-btn-outline text-xs" style={{ color: "var(--bloom-accent)", borderColor: "var(--bloom-accent)" }} data-testid="media-delete">
              <Trash2 size={12} /> Delete
            </button>
            <div className="flex gap-2">
              <button onClick={onClose} className="bloom-btn bloom-btn-ghost">Close</button>
              <button onClick={save} disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="media-save"><Edit3 size={14} /> {busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-[var(--bloom-ink-muted)] italic font-serif mt-1">{hint}</div>}
    </div>
  );
}

function fmtSize(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
